/**
 * Reports Page - Category, Monthly, and User spending reports (Mobile Responsive)
 * Includes both Expense and Income reports with financial summary
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { api, type Receipt, type Category, type User, type FinancialSummary, type Income, type IncomeCategory } from '../lib/api';
import { useOrganizationChange } from '../hooks/useOrganizationChange';

type ReportType = 'category' | 'monthly' | 'user';
type DataType = 'expense' | 'income';

interface CategoryReport {
  categoryId: string;
  categoryName: string;
  count: number;
  total: number;
  percentage: number;
}

interface MonthlyReport {
  month: string;
  monthLabel: string;
  count: number;
  total: number;
}

interface UserReport {
  userId: string;
  username: string;
  count: number;
  total: number;
  percentage: number;
}

interface IncomeCategoryReport {
  categoryId: string;
  categoryName: string;
  count: number;
  total: number;
  percentage: number;
}

interface IncomeMonthlyReport {
  month: string;
  monthLabel: string;
  count: number;
  total: number;
}

interface IncomeUserReport {
  userId: string;
  username: string;
  count: number;
  total: number;
  percentage: number;
}

export function ReportsPage() {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [incomeCategories, setIncomeCategories] = useState<IncomeCategory[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reportType, setReportType] = useState<ReportType>('category');
  const [dataType, setDataType] = useState<DataType>('expense');
  const [showFilters, setShowFilters] = useState(false);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary | null>(null);

  // Date range filter
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Refresh when organization changes
  useOrganizationChange(() => {
    setRefreshKey(k => k + 1);
  });

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [
          receiptsResult,
          incomesResult,
          categoriesResult,
          incomeCategoriesResult,
          usersResult,
          financialResult,
        ] = await Promise.all([
          api.getReceipts({ limit: 1000, startDate, endDate }),
          api.getIncomes({ limit: 1000, startDate, endDate }),
          api.getCategories(),
          api.getIncomeCategories(),
          api.getUsers(),
          api.getFinancialSummary({ startDate, endDate }),
        ]);

        if (receiptsResult.success && receiptsResult.data) {
          setReceipts(receiptsResult.data.receipts);
        }
        if (incomesResult.success && incomesResult.data) {
          setIncomes(incomesResult.data.income);
        }
        if (categoriesResult.success && categoriesResult.data) {
          setCategories(categoriesResult.data.categories);
        }
        if (incomeCategoriesResult.success && incomeCategoriesResult.data) {
          setIncomeCategories(incomeCategoriesResult.data.categories);
        }
        if (usersResult.success && usersResult.data) {
          setUsers(usersResult.data.users);
        }
        if (financialResult.success && financialResult.data) {
          setFinancialSummary(financialResult.data);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [startDate, endDate, refreshKey]);

  // Category Report
  const categoryReport = useMemo((): CategoryReport[] => {
    const totals = new Map<string, { count: number; total: number }>();
    const grandTotal = receipts.reduce((sum, r) => sum + r.total, 0);

    receipts.forEach((receipt) => {
      const catId = receipt.categoryId || 'uncategorized';
      const current = totals.get(catId) || { count: 0, total: 0 };
      totals.set(catId, {
        count: current.count + 1,
        total: current.total + receipt.total,
      });
    });

    return Array.from(totals.entries())
      .map(([categoryId, data]) => {
        const category = categories.find((c) => c.id === categoryId);
        return {
          categoryId,
          categoryName: category?.name || 'Uncategorized',
          count: data.count,
          total: data.total,
          percentage: grandTotal > 0 ? (data.total / grandTotal) * 100 : 0,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [receipts, categories]);

  // Monthly Report
  const monthlyReport = useMemo((): MonthlyReport[] => {
    const totals = new Map<string, { count: number; total: number }>();

    receipts.forEach((receipt) => {
      const date = new Date(receipt.date);
      const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const current = totals.get(month) || { count: 0, total: 0 };
      totals.set(month, {
        count: current.count + 1,
        total: current.total + receipt.total,
      });
    });

    return Array.from(totals.entries())
      .map(([month, data]) => {
        const [year, monthNum] = month.split('-');
        const monthLabel = new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
        });
        return {
          month,
          monthLabel,
          count: data.count,
          total: data.total,
        };
      })
      .sort((a, b) => b.month.localeCompare(a.month));
  }, [receipts]);

  // User Report
  const userReport = useMemo((): UserReport[] => {
    const totals = new Map<string, { count: number; total: number }>();
    const grandTotal = receipts.reduce((sum, r) => sum + r.total, 0);

    receipts.forEach((receipt) => {
      const current = totals.get(receipt.userId) || { count: 0, total: 0 };
      totals.set(receipt.userId, {
        count: current.count + 1,
        total: current.total + receipt.total,
      });
    });

    return Array.from(totals.entries())
      .map(([userId, data]) => {
        const user = users.find((u) => u.id === userId);
        return {
          userId,
          username: user?.username || 'Unknown',
          count: data.count,
          total: data.total,
          percentage: grandTotal > 0 ? (data.total / grandTotal) * 100 : 0,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [receipts, users]);

  // Income Category Report
  const incomeCategoryReport = useMemo((): IncomeCategoryReport[] => {
    const totals = new Map<string, { count: number; total: number }>();
    const grandTotal = incomes.reduce((sum, i) => sum + i.amount, 0);

    incomes.forEach((income) => {
      const catId = income.categoryId || 'uncategorized';
      const current = totals.get(catId) || { count: 0, total: 0 };
      totals.set(catId, {
        count: current.count + 1,
        total: current.total + income.amount,
      });
    });

    return Array.from(totals.entries())
      .map(([categoryId, data]) => {
        const category = incomeCategories.find((c) => c.id === categoryId);
        return {
          categoryId,
          categoryName: category?.name || 'Uncategorized',
          count: data.count,
          total: data.total,
          percentage: grandTotal > 0 ? (data.total / grandTotal) * 100 : 0,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [incomes, incomeCategories]);

  // Income Monthly Report
  const incomeMonthlyReport = useMemo((): IncomeMonthlyReport[] => {
    const totals = new Map<string, { count: number; total: number }>();

    incomes.forEach((income) => {
      const date = new Date(income.date);
      const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const current = totals.get(month) || { count: 0, total: 0 };
      totals.set(month, {
        count: current.count + 1,
        total: current.total + income.amount,
      });
    });

    return Array.from(totals.entries())
      .map(([month, data]) => {
        const [year, monthNum] = month.split('-');
        const monthLabel = new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
        });
        return {
          month,
          monthLabel,
          count: data.count,
          total: data.total,
        };
      })
      .sort((a, b) => b.month.localeCompare(a.month));
  }, [incomes]);

  // Income User Report
  const incomeUserReport = useMemo((): IncomeUserReport[] => {
    const totals = new Map<string, { count: number; total: number }>();
    const grandTotal = incomes.reduce((sum, i) => sum + i.amount, 0);

    incomes.forEach((income) => {
      const current = totals.get(income.userId) || { count: 0, total: 0 };
      totals.set(income.userId, {
        count: current.count + 1,
        total: current.total + income.amount,
      });
    });

    return Array.from(totals.entries())
      .map(([userId, data]) => {
        const user = users.find((u) => u.id === userId);
        return {
          userId,
          username: user?.username || 'Unknown',
          count: data.count,
          total: data.total,
          percentage: grandTotal > 0 ? (data.total / grandTotal) * 100 : 0,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [incomes, users]);

  const grandTotal = receipts.reduce((sum, r) => sum + r.total, 0);
  const totalCount = receipts.length;
  const incomeGrandTotal = incomes.reduce((sum, i) => sum + i.amount, 0);
  const incomeTotalCount = incomes.length;

  const formatCurrency = (amount: number) => `HKD ${amount.toFixed(2)}`;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Reports</h1>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="sm:hidden p-2 rounded-lg bg-white shadow text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
        </button>
      </div>

      {/* Date Range Filter */}
      <div className={`bg-white rounded-lg shadow p-4 mb-4 sm:mb-6 ${showFilters ? 'block' : 'hidden sm:block'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <span className="text-sm font-medium text-gray-700">Date Range:</span>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 flex-1">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
            />
            <span className="text-gray-500 text-center sm:text-left">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
            />
            <button
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      {financialSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-green-500">
            <div className="text-sm font-medium text-gray-500 mb-1">Total Income</div>
            <div className="text-2xl sm:text-3xl font-bold text-green-600 truncate">
              {formatCurrency(financialSummary.income.total)}
            </div>
            <div className="text-xs text-gray-400 mt-1">{financialSummary.income.count} entries</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-red-500">
            <div className="text-sm font-medium text-gray-500 mb-1">Total Expenses</div>
            <div className="text-2xl sm:text-3xl font-bold text-red-600 truncate">
              {formatCurrency(financialSummary.expenses.total)}
            </div>
            <div className="text-xs text-gray-400 mt-1">{financialSummary.expenses.count} receipts</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-blue-500">
            <div className="text-sm font-medium text-gray-500 mb-1">Net Balance</div>
            <div className={`text-2xl sm:text-3xl font-bold truncate ${
              financialSummary.net >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {financialSummary.net >= 0 ? '+' : ''}{formatCurrency(financialSummary.net)}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {financialSummary.net >= 0 ? 'Surplus' : 'Deficit'}
            </div>
          </div>
        </div>
      )}

      {/* Data Type Toggle */}
      <div className="flex gap-2 mb-4 sm:mb-6">
        <button
          onClick={() => setDataType('expense')}
          className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
            dataType === 'expense'
              ? 'bg-red-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          Expenses
        </button>
        <button
          onClick={() => setDataType('income')}
          className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
            dataType === 'income'
              ? 'bg-green-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          Income
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="text-sm font-medium text-gray-500 mb-1">
            {dataType === 'expense' ? 'Total Receipts' : 'Total Income Entries'}
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-gray-900">
            {dataType === 'expense' ? totalCount : incomeTotalCount}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="text-sm font-medium text-gray-500 mb-1">
            {dataType === 'expense' ? 'Total Spending' : 'Total Income'}
          </div>
          <div className={`text-2xl sm:text-3xl font-bold truncate ${
            dataType === 'expense' ? 'text-red-600' : 'text-green-600'
          }`}>
            {formatCurrency(dataType === 'expense' ? grandTotal : incomeGrandTotal)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
          <div className="text-sm font-medium text-gray-500 mb-1">
            {dataType === 'expense' ? 'Average per Receipt' : 'Average per Entry'}
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-blue-600 truncate">
            {formatCurrency(
              dataType === 'expense'
                ? (totalCount > 0 ? grandTotal / totalCount : 0)
                : (incomeTotalCount > 0 ? incomeGrandTotal / incomeTotalCount : 0)
            )}
          </div>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="flex gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
        {[
          { type: 'category' as ReportType, label: 'Category', fullLabel: 'By Category' },
          { type: 'monthly' as ReportType, label: 'Monthly', fullLabel: 'By Month' },
          { type: 'user' as ReportType, label: 'User', fullLabel: 'By User' },
        ].map((tab) => (
          <button
            key={tab.type}
            onClick={() => setReportType(tab.type)}
            className={`px-4 sm:px-6 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              reportType === tab.type
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span className="sm:hidden">{tab.label}</span>
            <span className="hidden sm:inline">{tab.fullLabel}</span>
          </button>
        ))}
      </div>

      {/* Report Content */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-6 sm:p-8 text-center text-gray-500">Loading...</div>
        ) : (dataType === 'expense' ? receipts.length === 0 : incomes.length === 0) ? (
          <div className="p-6 sm:p-8 text-center text-gray-500">
            No {dataType === 'expense' ? 'receipts' : 'income entries'} found for the selected date range
          </div>
        ) : (
          <>
            {/* Category Report - Mobile Cards */}
            {reportType === 'category' && (
              <>
                {/* Mobile View */}
                <div className="sm:hidden divide-y divide-gray-200">
                  {(dataType === 'expense' ? categoryReport : incomeCategoryReport).map((row) => (
                    <div key={row.categoryId} className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-gray-900">{row.categoryName}</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(row.total)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                        <span>{row.count} {dataType === 'expense' ? 'receipts' : 'entries'}</span>
                        <span>{row.percentage.toFixed(1)}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${dataType === 'expense' ? 'bg-red-600' : 'bg-green-600'}`}
                          style={{ width: `${row.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop View */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full min-w-[500px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Count
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          % of Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(dataType === 'expense' ? categoryReport : incomeCategoryReport).map((row) => (
                        <tr key={row.categoryId} className="hover:bg-gray-50">
                          <td className="px-4 lg:px-6 py-4 font-medium text-gray-900">{row.categoryName}</td>
                          <td className="px-4 lg:px-6 py-4 text-right text-gray-600">{row.count}</td>
                          <td className="px-4 lg:px-6 py-4 text-right font-semibold text-gray-900 whitespace-nowrap">
                            {formatCurrency(row.total)}
                          </td>
                          <td className="px-4 lg:px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 lg:w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${dataType === 'expense' ? 'bg-red-600' : 'bg-green-600'}`}
                                  style={{ width: `${row.percentage}%` }}
                                />
                              </div>
                              <span className="text-gray-600 w-12 text-right text-sm">
                                {row.percentage.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Monthly Report - Mobile Cards */}
            {reportType === 'monthly' && (
              <>
                {/* Mobile View */}
                <div className="sm:hidden divide-y divide-gray-200">
                  {(dataType === 'expense' ? monthlyReport : incomeMonthlyReport).map((row) => (
                    <div key={row.month} className="p-4">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-gray-900">{row.monthLabel}</span>
                        <span className={`font-semibold ${dataType === 'expense' ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(row.total)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>{row.count} {dataType === 'expense' ? 'receipts' : 'entries'}</span>
                        <span>Avg: {formatCurrency(row.count > 0 ? row.total / row.count : 0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop View */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full min-w-[500px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Month
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Count
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Avg per {dataType === 'expense' ? 'Receipt' : 'Entry'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(dataType === 'expense' ? monthlyReport : incomeMonthlyReport).map((row) => (
                        <tr key={row.month} className="hover:bg-gray-50">
                          <td className="px-4 lg:px-6 py-4 font-medium text-gray-900">{row.monthLabel}</td>
                          <td className="px-4 lg:px-6 py-4 text-right text-gray-600">{row.count}</td>
                          <td className={`px-4 lg:px-6 py-4 text-right font-semibold whitespace-nowrap ${
                            dataType === 'expense' ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {formatCurrency(row.total)}
                          </td>
                          <td className="px-4 lg:px-6 py-4 text-right text-gray-600 whitespace-nowrap">
                            {formatCurrency(row.count > 0 ? row.total / row.count : 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* User Report - Mobile Cards */}
            {reportType === 'user' && (
              <>
                {/* Mobile View */}
                <div className="sm:hidden divide-y divide-gray-200">
                  {(dataType === 'expense' ? userReport : incomeUserReport).map((row) => (
                    <div key={row.userId} className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-gray-900">{row.username}</span>
                        <span className={`font-semibold ${dataType === 'expense' ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(row.total)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                        <span>{row.count} {dataType === 'expense' ? 'receipts' : 'entries'}</span>
                        <span>{row.percentage.toFixed(1)}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${dataType === 'expense' ? 'bg-red-600' : 'bg-green-600'}`}
                          style={{ width: `${row.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop View */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full min-w-[500px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Count
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          % of Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(dataType === 'expense' ? userReport : incomeUserReport).map((row) => (
                        <tr key={row.userId} className="hover:bg-gray-50">
                          <td className="px-4 lg:px-6 py-4 font-medium text-gray-900">{row.username}</td>
                          <td className="px-4 lg:px-6 py-4 text-right text-gray-600">{row.count}</td>
                          <td className={`px-4 lg:px-6 py-4 text-right font-semibold whitespace-nowrap ${
                            dataType === 'expense' ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {formatCurrency(row.total)}
                          </td>
                          <td className="px-4 lg:px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 lg:w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${dataType === 'expense' ? 'bg-red-600' : 'bg-green-600'}`}
                                  style={{ width: `${row.percentage}%` }}
                                />
                              </div>
                              <span className="text-gray-600 w-12 text-right text-sm">
                                {row.percentage.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
